<script setup lang="ts">
/**
 * คู่มือปิดการขาย (owner 18 ต.ค. 69 "ตอบไปแล้วเหมือนระบบไม่ทำงานต่อ"): the draft box when the customer is waiting and the
 * script has nothing to suggest for it — the reply the playbook has no answer for ("ลูกค้าถามกลับ: “…”"), or no
 * suggestion at all — never an empty box: the step now with its question ("ใส่ในช่องพิมพ์", after what is typed, never
 * sent) and "ให้ AI พาไปขั้นต่อไป" (on press only: one message that answers the customer, then at most one question toward
 * this step — stores/script.ts nextStep). Rendered by DraftBox when stores/script.ts `stepCard()` gives something.
 */
import { computed } from 'vue'
import { NEXT_STEP_TITLE } from '../../lib/script'
import type { StepCard } from '../../stores/script'
import { useScript } from '../../stores/script'
import Icon from '../ui/Icon.vue'

const props = defineProps<{ convId: string; card: StepCard; canAi: boolean }>()
const script = useScript()

const pb = computed(() => props.card.playbook)
/** "ลูกค้าถามกลับ" / "ลูกค้าตอบกลับ" + the words, when the script read the reply. */
const lead = computed(() => (pb.value ? (pb.value.question ? 'ลูกค้าถามกลับ' : 'ลูกค้าตอบกลับ') : null))
const inBook = computed(() => !!pb.value && pb.value.type !== 'other')
</script>

<template>
  <div class="stc" role="region" aria-label="ขั้นตอนนี้">
    <div class="stc-h">
      <span class="stc-chip"><Icon name="target" />ขั้นตอนนี้</span>
      <span v-if="card.step" class="stc-step">{{ card.step.label }}</span>
      <span class="grow"></span>
      <span v-if="pb" class="stc-tag" :class="{ out: !inBook }">{{ inBook ? `คู่มือ: ${pb.label}` : 'ยังไม่มีในคู่มือ' }}</span>
      <button type="button" class="stc-off" title="ปิดกล่องแนะนำให้ตอบทุกแชท (เฉพาะคุณ)" @click="script.setSuggestOff(true)">ปิดคำแนะนำ</button>
    </div>
    <p v-if="lead" class="stc-r">
      <Icon name="reply" /><span><b>{{ lead }}:</b> “{{ pb!.text }}”</span>
    </p>
    <p v-if="card.step?.question" class="stc-q" aria-label="คำถามของขั้นนี้">{{ card.step.question }}</p>
    <div class="stc-a">
      <button v-if="card.step?.question" type="button" class="stc-b" @click="script.insertStep(convId, card.step.key)">
        <Icon name="arrowDown" />ใส่คำถามในช่องพิมพ์
      </button>
      <button v-if="canAi" type="button" class="stc-b pri" :title="NEXT_STEP_TITLE" @click="script.nextStep(convId)">
        <Icon name="sparkles" />ให้ AI พาไปขั้นต่อไป
      </button>
    </div>
  </div>
</template>

<style scoped>
.stc {
  padding: 10px 12px 12px;
  border-radius: 11px 11px 0 0;
  background: #fbfdfc;
  box-shadow: inset 0 0 0 1.5px #9fcdb8;
}
.grow {
  flex: 1;
}
.stc-h {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px 8px;
  min-width: 0;
}
.stc-chip {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13.5px;
  font-weight: 700;
  line-height: 20px;
  color: var(--ac2);
  white-space: nowrap;
}
.stc-chip :deep(.ic) {
  width: 15px;
  height: 15px;
  color: var(--ac);
}
.stc-step {
  min-width: 0;
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
  color: var(--ink);
}
.stc-off {
  flex: none;
  height: 24px;
  padding: 0 8px;
  border-radius: 6px;
  color: var(--mut);
  font-size: 12px;
  line-height: 24px;
}
.stc-off:hover {
  background: #eef2f0;
  color: var(--ink);
}
.stc-tag {
  flex: none;
  height: 22px;
  padding: 0 8px;
  border-radius: 11px;
  background: var(--acs);
  color: var(--ac2);
  font-size: 12px;
  font-weight: 600;
  line-height: 22px;
  white-space: nowrap;
}
.stc-tag.out {
  background: var(--warns);
  color: var(--warn);
}
.stc-r {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-top: 6px;
  font-size: 13px;
  line-height: 20px;
  color: var(--ink);
  overflow-wrap: anywhere;
}
.stc-r b {
  font-weight: 600;
}
.stc-r :deep(.ic) {
  flex: none;
  width: 14px;
  height: 14px;
  margin-top: 3px;
  color: var(--mut);
}
.stc-q {
  margin-top: 6px;
  padding: 7px 10px;
  border-radius: 8px;
  background: #fff;
  border: 1px solid #dfe8e3;
  font-size: 14px;
  line-height: 22px;
  color: var(--ink);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
.stc-a {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 8px;
  margin-top: 10px;
}
.stc-b {
  height: 32px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid #c3cdc8;
  background: #fff;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
  color: var(--ink);
  white-space: nowrap;
}
.stc-b:hover {
  background: #f4f7f5;
}
.stc-b :deep(.ic) {
  width: 14px;
  height: 14px;
  color: var(--ac);
}
.stc-b.pri {
  border-color: var(--ac);
  background: var(--ac);
  color: #fff;
}
.stc-b.pri:hover {
  background: var(--ac2);
  border-color: var(--ac2);
}
.stc-b.pri :deep(.ic) {
  color: #fff;
}
</style>
